/**
 * Centralized API Interceptor
 * Handles ALL API calls and session validation in one place
 * No need to update individual API calls
 */

import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

/**
 * Setup global fetch interceptor
 * This intercepts ALL fetch requests and responses
 */
export const setupAPIInterceptor = (getAccessTokenSilently, logout) => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

  // Store original fetch
  const originalFetch = window.fetch;

  // Override global fetch
  window.fetch = async (...args) => {
    let [url, options = {}] = args;

    // Only intercept API calls to our backend
    if (url.includes(API_BASE_URL)) {
      try {
        // Automatically add Auth0 token to all API requests
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          }
        });

        options.headers = {
          ...options.headers,
          'Authorization': `Bearer ${token}`
        };
      } catch (error) {
        console.error('Failed to get access token:', error);
        // If token retrieval fails, trigger logout
        if (error.error === 'login_required' || error.error === 'consent_required') {
          logout({ logoutParams: { returnTo: window.location.origin } });
          throw new Error('Session expired. Please log in again.');
        }
      }
    }

    // Make the actual request
    const response = await originalFetch(url, options);

    // Only check responses from our API
    if (url.includes(API_BASE_URL)) {
      // Check session status from response headers
      const sessionStatus = response.headers.get('X-Session-Status');
      const tokenExpiring = response.headers.get('X-Token-Expiring');
      const tokenExpiresIn = response.headers.get('X-Token-Expires-In');

      // Handle session expiration
      if (sessionStatus === 'expired' || response.status === 401) {
        const clonedResponse = response.clone();
        const data = await clonedResponse.json().catch(() => ({}));
        
        // Check if backend says we should logout
        if (data.shouldLogout || response.status === 401) {
          console.warn('Session expired, logging out...', data.errorType);
          logout({ logoutParams: { returnTo: window.location.origin } });
          throw new Error(data.message || 'Session expired. Please log in again.');
        }
      }

      // Log token expiring warning (can be used to show UI warning)
      if (tokenExpiring === 'true') {
        console.warn(`Token expiring in ${tokenExpiresIn} seconds`);
        // Dispatch custom event that components can listen to
        window.dispatchEvent(new CustomEvent('tokenExpiring', { 
          detail: { expiresIn: parseInt(tokenExpiresIn) } 
        }));
      }
    }

    return response;
  };

  // Return cleanup function to restore original fetch
  return () => {
    window.fetch = originalFetch;
  };
};

/**
 * React Hook to setup API interceptor
 * Use this ONCE in your App.js
 */
export const useAPIInterceptor = () => {
  const { getAccessTokenSilently, logout, isAuthenticated } = useAuth0();

  useEffect(() => {
    if (!isAuthenticated) return;

    console.log('🔒 API Interceptor initialized - All API calls are now monitored');
    const cleanup = setupAPIInterceptor(getAccessTokenSilently, logout);

    return () => {
      console.log('🔓 API Interceptor cleaned up');
      cleanup();
    };
  }, [getAccessTokenSilently, logout, isAuthenticated]);
};

/**
 * Alternative: Axios Interceptor (if you use Axios)
 */
export const setupAxiosInterceptor = (axiosInstance, getAccessTokenSilently, logout) => {
  // Request interceptor - add token to all requests
  const requestInterceptor = axiosInstance.interceptors.request.use(
    async (config) => {
      try {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          }
        });
        config.headers.Authorization = `Bearer ${token}`;
      } catch (error) {
        console.error('Failed to get access token:', error);
        if (error.error === 'login_required' || error.error === 'consent_required') {
          logout({ logoutParams: { returnTo: window.location.origin } });
        }
        return Promise.reject(error);
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - check session status
  const responseInterceptor = axiosInstance.interceptors.response.use(
    (response) => {
      // Check session status headers
      const sessionStatus = response.headers['x-session-status'];
      const tokenExpiring = response.headers['x-token-expiring'];
      const tokenExpiresIn = response.headers['x-token-expires-in'];

      if (sessionStatus === 'expired') {
        console.warn('Session expired from header');
        logout({ logoutParams: { returnTo: window.location.origin } });
        return Promise.reject(new Error('Session expired'));
      }

      if (tokenExpiring === 'true') {
        console.warn(`Token expiring in ${tokenExpiresIn} seconds`);
        window.dispatchEvent(new CustomEvent('tokenExpiring', { 
          detail: { expiresIn: parseInt(tokenExpiresIn) } 
        }));
      }

      return response;
    },
    (error) => {
      // Handle 401 errors
      if (error.response?.status === 401) {
        const shouldLogout = error.response?.data?.shouldLogout;
        if (shouldLogout) {
          console.warn('401 Unauthorized - logging out');
          logout({ logoutParams: { returnTo: window.location.origin } });
        }
      }
      return Promise.reject(error);
    }
  );

  // Return cleanup function
  return () => {
    axiosInstance.interceptors.request.eject(requestInterceptor);
    axiosInstance.interceptors.response.eject(responseInterceptor);
  };
};

/**
 * React Hook for Axios interceptor
 */
export const useAxiosInterceptor = (axiosInstance) => {
  const { getAccessTokenSilently, logout, isAuthenticated } = useAuth0();

  useEffect(() => {
    if (!isAuthenticated || !axiosInstance) return;

    console.log('🔒 Axios Interceptor initialized');
    const cleanup = setupAxiosInterceptor(axiosInstance, getAccessTokenSilently, logout);

    return () => {
      console.log('🔓 Axios Interceptor cleaned up');
      cleanup();
    };
  }, [axiosInstance, getAccessTokenSilently, logout, isAuthenticated]);
};
